import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PerformanceStylesCSS } from './Performance.styled';
import { ADMIN_DASHBOARD_READABLE_LAST_UPDATED_OPTIONS_YEAR, AdminDashboards } from '../../../variables/constant';
import { DashboardContext, DashboardContextInit, DashboardProps } from '../admin-analytics-hook';
import InsufficientDataBackdrop from '../InsufficientDataBackdrop';
import AnalyticsFilters from '../AnalyticsFilters';
import OverallPerformanceWidget from './OverallPerformanceWidget';
import PerformanceWidget from './PerformanceWidget';
import TasksAssignedAndCompletedWidget from './TasksAssignedAndCompletedWidget';
import StudentCompletionRateWidget from './StudentCompletionRateWidget';
import Loader from '../../../components/Loader/loader';
import { toReadableFormat } from '../../../utils/dateFormat';
import TypeformEmbed from '../../../components/TypeformEmbed';
import usePerformance from './performance-hook';
import { useImitationProvider } from '../../../core/imitationContext';

const Performance = ({
  isDistrictAdmin,
  myDistrictDataLoading,
  myDistrictData,
  isSchoolAdmin,
  mySchoolDataLoading,
  mySchoolData,
  academicSessionLoading,
  academicSession,
  lastSnowflakeUpdateTimeData,
  getLastSnowflakeUpdateTimeLoading,
}: DashboardProps) => {
  const theme = useTheme();
  const styles = PerformanceStylesCSS(theme);
  const {
    loading, setLoading,
    getLocalStorageDashboardFilters, setLocalStorageDashboardFilters, localDashboardFilters,
    dashboardFilters,
    currentFilters, setCurrentFilters,
    analyticsFilters, setAnalyticsFilters,
    startDateInit,
    startDateSelected, setStartDateSelected,
    endDateSelected, setEndDateSelected,
    dashboardWidgetsDataStatus, setDashboardWidgetsDataStatus,
    isDataMissingInAllWidgets, setIsDataMissingInAllWidgets,
    areWidgetsLoading, setAreWidgetsLoading,
  } = DashboardContextInit({
    dashboardWidgets: AdminDashboards.performance,
    isDistrictAdmin,
    myDistrictDataLoading,
    myDistrictData,
    isSchoolAdmin,
    mySchoolDataLoading,
    mySchoolData,
    academicSessionLoading,
    academicSession,
    lastSnowflakeUpdateTimeData,
    getLastSnowflakeUpdateTimeLoading,
  });
  
  const { typeformData, onTypeformReady, onTypeformSubmit } = usePerformance();

  const { getImitationUserType } = useImitationProvider();
  const isNotImitated = !getImitationUserType();


  return (
    <DashboardContext.Provider value={{
      loading, setLoading,
      isSchoolAdmin, mySchoolData,
      isDistrictAdmin, myDistrictData,
      dashboardFilters,
      lastSnowflakeUpdateTimeData,
      getLastSnowflakeUpdateTimeLoading,
      currentFilters, setCurrentFilters,
      analyticsFilters, setAnalyticsFilters,
      academicSessionLoading, academicSession, startDateInit,
      startDateSelected, setStartDateSelected,
      endDateSelected, setEndDateSelected,
      dashboardWidgetsDataStatus, setDashboardWidgetsDataStatus,
      isDataMissingInAllWidgets, setIsDataMissingInAllWidgets,
      areWidgetsLoading, setAreWidgetsLoading,
      getLocalStorageDashboardFilters, setLocalStorageDashboardFilters, localDashboardFilters,
    }}>
      <Loader open={loading || !academicSession || !analyticsFilters || getLastSnowflakeUpdateTimeLoading} />
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        sx={styles.dashBoardContainer}
        id="performance-dashboard"
      >
        <Box sx={{ marginBottom: 2 }} id="performance-dashboard-header">
          <Typography variant="pageTitle">
            Performance Data Dashboard
          </Typography>
          <Box>
            {!!lastSnowflakeUpdateTimeData && <Typography variant="toolTipGreyText">
              Last Updated: {toReadableFormat(lastSnowflakeUpdateTimeData, ADMIN_DASHBOARD_READABLE_LAST_UPDATED_OPTIONS_YEAR, 'en-US').replace('at', '')} UTC
            </Typography>}
          </Box>
        </Box>
        {!!academicSession && !!analyticsFilters && (
          <Box>
            <Grid container spacing={2} item xs={12} sm={12} md={12} lg={12} p={2}>
              <AnalyticsFilters />
            </Grid>
            <Grid container spacing={2} sx={{ position: 'relative' }}>
              <InsufficientDataBackdrop dataMissing={!loading! && isDataMissingInAllWidgets!} />
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <OverallPerformanceWidget />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <StudentCompletionRateWidget />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <PerformanceWidget
                  enableTooltip
                  alignTitle
                  tooltipTitle={AdminDashboards.performance.PerformanceSliderWidget.tooltipText}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <TasksAssignedAndCompletedWidget />
              </Grid>
            </Grid>
          </Box>
        )}
        {
          !areWidgetsLoading &&
            isNotImitated &&
            !loading &&
            typeformData?.show_typeform && <TypeformEmbed
            formId={typeformData?.typeform_form_id}
            typeformLayout='popup'
            onCloseHandler={onTypeformReady}
            onFormSubmittedHandler={onTypeformSubmit}
            hiddenButton={true}
            buttonText='Feedback Form'/>
        }
      </Box>
    </DashboardContext.Provider>
  );
};

export default Performance;
